export const renderRegexHighlight = (text, lang = 'ru', options = {}) => {
  if (!text) return null;

  const { inheritTextMetrics = false, spaceMarkerMode = 'glyph' } = options;

  const result = [];
  let i = 0;
  let key = 0;

  const REGEX_BG = 'var(--color-primary-alpha)';
  const BASE_COLOR = 'var(--color-text-black)';
  const ALT_COLOR = 'var(--color-red-highlight)';

  const TEXT_STYLE = inheritTextMetrics
    ? {
        fontFamily: 'inherit',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
      }
    : {
        fontSize: 'var(--font-size-s)',
        letterSpacing: '0.13132px',
      };

  const isRu = lang === 'ru';

  const getLetterColor = char => {
    const isCyrillic = /[а-яёіїєґ]/i.test(char);
    const isLatin = /[a-z]/i.test(char);

    if (isRu) {
      if (isCyrillic) return BASE_COLOR;
      if (isLatin) return ALT_COLOR;
    } else {
      if (isLatin) return BASE_COLOR;
      if (isCyrillic) return ALT_COLOR;
    }

    return BASE_COLOR;
  };

  const mark = content => (
    <span
      key={key++}
      style={{
        ...TEXT_STYLE,
        color: BASE_COLOR,
        backgroundColor: REGEX_BG,
      }}
    >
      {content}
    </span>
  );

  const plain = (content, color = BASE_COLOR) => (
    <span key={key++} style={{ ...TEXT_STYLE, color }}>
      {content}
    </span>
  );

  const markedSpace = () => (
    <span
      key={key++}
      style={{
        ...TEXT_STYLE,
        color: BASE_COLOR,
        backgroundColor: REGEX_BG,
        // Сохраняем реальную ширину пробела и рисуем точку по центру.
        backgroundImage:
          'radial-gradient(circle, currentColor 1px, transparent 1.6px)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center 62%',
      }}
    >
      {' '}
    </span>
  );

  while (i < text.length) {
    const char = text[i];

    // ===== REGEX BLOCKS (без изменений) =====
    if (char === '\\') {
      const next = text[i + 1];

      if (next === undefined) {
        result.push(mark('\\'));
        i++;
        continue;
      }

      if (/\d/.test(next)) {
        let num = next;
        i += 2;

        while (i < text.length && /\d/.test(text[i])) {
          num += text[i];
          i++;
        }

        result.push(mark(`\\${num}`));
        continue;
      }

      result.push(mark(`\\${next}`));
      i += 2;
      continue;
    }

    if (char === '[') {
      let j = i + 1;
      while (j < text.length && text[j] !== ']') j++;

      if (j >= text.length) {
        result.push(mark('['));
        i++;
        continue;
      }

      const inner = text.slice(i + 1, j);

      result.push(mark('['));
      result.push(
        <span
          key={key++}
          style={{
            ...TEXT_STYLE,
            color: BASE_COLOR,
            backgroundColor: REGEX_BG,
          }}
        >
          {inner}
        </span>,
      );
      result.push(mark(']'));

      i = j + 1;
      continue;
    }

    if (char === '(' || char === ')') {
      result.push(mark(char));
      i++;
      continue;
    }

    if (char === '{') {
      let j = i + 1;
      while (j < text.length && text[j] !== '}') j++;

      if (j >= text.length) {
        result.push(plain('{', ALT_COLOR));
        i++;
        continue;
      }

      result.push(mark(text.slice(i, j + 1)));
      i = j + 1;
      continue;
    }

    if (['+', '*', '?', '.', '|', '^', '$'].includes(char)) {
      result.push(mark(char));
      i++;
      continue;
    }

    if (char === ' ') {
      if (spaceMarkerMode === 'none') {
        result.push(plain(' '));
      } else {
        result.push(
          spaceMarkerMode === 'native-width' ? markedSpace() : mark('·'),
        );
      }
      i++;
      continue;
    }

    if (char === '\n') {
      result.push(mark('↵\n'));
      i++;
      continue;
    }

    // ===== LETTERS =====
    if (/[а-яёіїєґa-z]/i.test(char)) {
      result.push(plain(char, getLetterColor(char)));
      i++;
      continue;
    }

    // ===== NON-LETTER (НЕ regex) =====
    // всё что осталось: @ % _ - и т.д.
    result.push(plain(char, ALT_COLOR));
    i++;
  }

  return result;
};

export const renderLetterHighlight = (text, lang = 'ru') => {
  if (!text) return null;

  return text.split('').map((char, index) => {
    const isCyrillic = /[а-яёіїєґ]/i.test(char);
    const isLatin = /[a-z]/i.test(char);
    const isDigit = /\d/.test(char);

    if (lang === 'ru') {
      if (isCyrillic) {
        return (
          <span
            key={index}
            style={{ color: 'var(--color-text-black)', fontSize: '14px' }}
          >
            {char}
          </span>
        );
      }
      if (isLatin || isDigit) {
        return (
          <span
            key={index}
            style={{ color: 'var(--color-red-highlight)', fontSize: '14px' }}
          >
            {char}
          </span>
        );
      }
    } else if (lang === 'en') {
      if (isLatin) {
        return (
          <span
            key={index}
            style={{ color: 'var(--color-text-black)', fontSize: '14px' }}
          >
            {char}
          </span>
        );
      }
      if (isCyrillic || isDigit) {
        return (
          <span
            key={index}
            style={{ color: 'var(--color-red-highlight)', fontSize: '14px' }}
          >
            {char}
          </span>
        );
      }
    }

    return <span key={index}>{char}</span>;
  });
};
